import * as React from 'react';
import Card from 'antd/lib/card';
import Space from 'antd/lib/space';
import Checkbox from 'antd/lib/checkbox';
import Radio from 'antd/lib/radio';
import Search from 'antd/lib/input/Search';
import Table, { ColumnProps } from 'antd/lib/table';
import { TableRowSelection } from 'antd/lib/table/interface';
import Typography from 'antd/lib/typography';
import Button from 'antd/lib/button';
import Alert from 'antd/lib/alert';

import { WithId } from '@common/typescript/objects/WithId';
import { Nullable } from '@common/typescript/objects/Nullable';

import { Stack, StackDirection } from '@app/components/Layout/Stack';

export interface DataSource<T> {
	list: Array<T>;
	total: number;
	loading?: boolean;
}

export type KeyExtractor<T, K> = (item: T) => K;

export interface TransferListProps<T, K extends React.Key = number> {
	title: React.ReactNode;
	extra?: React.ReactNode;

	data: DataSource<T>;
	columns: Array<ColumnProps<T>>;
	error?: Nullable<string>;

	keyExtractor?: KeyExtractor<T, K>;

	query?: string;
	onQuerySearch?: (value: string) => void;
	searchPlaceholder?: string;

	action?: string;
	icon?: React.ReactNode;
	type?: 'link' | 'danger';

	mode?: 'single' | 'multiple';
	onSelectionChange?: (value: Array<T>) => void;
	useAntdRowSelection?: boolean;
	renderSelectionControl?: (selected: boolean, toggle: () => void, item: T) => React.ReactNode;
	clearSelectionOnAction?: boolean;

	pagination?: {
		perPage?: number;
		page?: number;
		onChange?: (value: number) => void;
	};
}

function setKey<K extends React.Key, T>(map: Map<K, T>, key: K, value: T) {
	if (map.has(key)) return map;

	const r = new Map(map);
	r.set(key, value);

	return r;
}

function deleteKey<K extends React.Key, T>(map: Map<K, T>, key: K) {
	if (!map.has(key)) return map;

	const r = new Map(map);
	r.delete(key);

	return r;
}

function toggleKey<K extends React.Key, T>(map: Map<K, T>, key: K, value: T, mode: 'single' | 'multiple') {
	if (mode !== 'single') return map.has(key) ? deleteKey(map, key) : setKey(map, key, value);

	const r = new Map<K, T>();
	if (!map.has(key)) {
		r.set(key, value);
	}

	return r;
}

export const TransferList = <T extends WithId, K extends React.Key = number>(props: TransferListProps<T, K>) => {
	const {
		data,
		columns: userColumns,
		keyExtractor = ((item: WithId) => item.id),
		mode = 'multiple',
		renderSelectionControl,
		useAntdRowSelection = false,
		searchPlaceholder = 'Search',
		onSelectionChange,
		clearSelectionOnAction = true,
	} = props;

	const [selection, setSelection] = React.useState<Map<number | K, T>>(new Map());
	const selectedKeys = React.useMemo<Array<React.Key>>(() => Array.from(selection.keys()), [selection]);

	const onSelected = React.useCallback(() => {
		const current = selection;
		if (clearSelectionOnAction) setSelection(new Map());
		if (!onSelectionChange) return;
		onSelectionChange(Array.from(current.values()));
	}, [selection, onSelectionChange, clearSelectionOnAction]);

	const columns = React.useMemo<Array<ColumnProps<T>>>(() => {
		if (useAntdRowSelection) return userColumns;

		const selectionColumn: ColumnProps<T> = {
			title: '',
			dataIndex: '__selection__' as never,
			width: 48,
			align: 'left',
			render: (value: number, record: T) => {
				const key = keyExtractor(record);
				const checked = selection.has(key);
				const toggle = () => setSelection(toggleKey(selection, key, record, mode));

				if (renderSelectionControl) return renderSelectionControl(checked, toggle, record);

				const Control = mode === 'multiple' ? Checkbox : Radio;

				return (
					<Control
						checked={checked}
						onChange={toggle}
					/>
				);
			},
		};

		return [selectionColumn, ...userColumns];
	}, [userColumns, selection, keyExtractor, mode, renderSelectionControl, useAntdRowSelection]);

	const rowSelection = React.useMemo<TableRowSelection<T> | undefined>(() => {
		if (!useAntdRowSelection) return undefined;

		const rowType: 'checkbox' | 'radio' = mode === 'multiple' ? 'checkbox' : 'radio';

		return {
			type: rowType,
			selectedRowKeys: selectedKeys,
			onChange: (keys: Array<React.Key>, rows: Array<T>) => {
				const kmap = new Map<K | number, T>();
				rows.forEach((r) => kmap.set(keyExtractor(r), r));
				setSelection(kmap);
			},
		};
	}, [useAntdRowSelection, mode, selectedKeys, keyExtractor]);

	return (
		<Card
			title={(
				<Space direction="vertical" size={0}>
					<Typography.Text>{props.title}</Typography.Text>
					<Typography.Text style={{ fontWeight: 'normal', opacity: 0.6 }}>
						{data.total} item{data.total !== 1 ? 's' : ''}
					</Typography.Text>
				</Space>
			)}
			extra={props.extra}
			size="small"
			style={{ height: '100%' }}
		>
			<Space direction="vertical" size="small">
				<Stack direction={StackDirection.Horizontal} gap={2} style={{ justifyContent: 'space-between' }}>
					<Typography.Text>{selection.size} selected</Typography.Text>
					{
						props.action ? (
							<Button
								type={props.type === 'link' ? 'link' : undefined}
								icon={props.icon}
								danger={props.type === 'danger'}
								disabled={!selection.size}
								onClick={onSelected}
							>
								{props.action}
							</Button>
						) : null
					}
				</Stack>

				<Search placeholder={searchPlaceholder} size="middle" key={props.query} defaultValue={props.query} onSearch={(v) => props.onQuerySearch?.(v)} />

				{props.error ? <Alert type="error" message={props.error} /> : null}

				<Table
					rowKey={(record: T) => keyExtractor(record)}
					size="small"
					columns={columns}
					dataSource={data.list}
					loading={data.loading}
					pagination={{
						total: data.total,
						hideOnSinglePage: true,
						position: ['bottomCenter'],

						current: props.pagination?.page,
						pageSize: props.pagination?.perPage,
						onChange: props.pagination?.onChange,

						showSizeChanger: false,
					}}
					bordered={false}
					onRow={(dataRow: T) => ({ onClick: () => setSelection(toggleKey(selection, keyExtractor(dataRow), dataRow, mode)) })}
					expandable={{
						rowExpandable: () => false,
						expandedRowRender: () => <Typography.Text>Items</Typography.Text>,
					}}
					rowSelection={rowSelection}
				/>
			</Space>
		</Card>
	);
};
